import React, { useEffect, useState } from 'react';
import {
    Grid,
    Card,
    FormControl,
    FormLabel,
    Autocomplete,
    AutocompleteOption,
    Button,
    Typography,
    Stack,
    Box,
} from '@mui/joy';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useEventForm } from '../../context/EventFormContext';
import { post } from '../../api/api';

const CategoriesStep: React.FC = () => {
    const { thirdFormData, setThirdFormData, searchCateg, setStep, validateStep3 } = useEventForm();
    const [recommendedCategories, setRecommendedCategories] = useState<any[]>([]);

    /**
     * Appel API pour récupérer des recommandations basées sur les sous‑catégories sélectionnées.
     * Ici, on envoie la liste des UUID (champ "id") des objets sélectionnés.
     */
    const fetchRecommendations = async () => {
        const ids = thirdFormData
            .map((item: any) => item?.id)
            .filter((id: string | undefined) => id !== undefined);

        if (ids.length > 0) {
            const formValues = {
                functions: [
                    {
                        name: 'getSuggestSubCategoriesBySubCategories',
                        parameters: { ids },
                    },
                ],
            };

            try {
                const res = await post({ url: 'functions', data: formValues });
                const results = res?.data.functions?.[0]?.results || [];
                setRecommendedCategories(results);
            } catch (error) {
                console.error('Error fetching recommendations:', error);
                setRecommendedCategories([]);
            }
        } else {
            setRecommendedCategories([]);
        }
    };

    // Déclencher l'appel API dès que thirdFormData change
    useEffect(() => {
        fetchRecommendations();
    }, [thirdFormData]);

    /**
     * Lorsqu'une recommandation est cliquée, on recherche dans searchCateg
     * l'élément dont le champ "slug" correspond à celui de la recommandation et on l'ajoute à thirdFormData.
     */
    const handleAddRecommendedCategory = (rec: any) => {
        const recSlug = rec?.subCategory?.properties?.slug;
        if (!recSlug) return;

        const matchingOption = searchCateg.find(
            (option: any) => option.slug === recSlug
        );

        if (
            matchingOption &&
            !thirdFormData.some((item: any) => item.slug === recSlug)
        ) {
            setThirdFormData([...thirdFormData, matchingOption]);
        }
    };

    return (
        <>
            {/* Navigation */}
            <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Button
                    size="lg"
                    variant="outlined"
                    color="neutral"
                    onClick={() => setStep((prev: number) => prev - 1)}
                    startDecorator={<ChevronLeft />}
                >
                    Précédent
                </Button>
                <Button
                    size="lg"
                    variant="solid"
                    onClick={async () => {
                        if (await validateStep3()) {
                            setStep((prev) => prev + 1)
                        }
                    }} endDecorator={<ChevronRight />}
                >
                    Suivant
                </Button>
            </Stack>

            <Card variant="outlined" sx={{ p: 3, mt: 2 }}>
                <Typography level="h4" sx={{ mb: 2 }}>
                    Catégories
                </Typography>
                <Grid container spacing={2}>
                    {/* Sélecteur basé sur searchCateg */}
                    <Grid xs={12}>
                        <Card variant="soft" sx={{ mx: 5, p: 5 }}>
                            <FormControl>
                                <FormLabel>Ajouter une catégorie</FormLabel>
                                <Autocomplete
                                    autoComplete
                                    options={searchCateg}
                                    value={thirdFormData}
                                    multiple
                                    onChange={(_, newValue) => setThirdFormData(newValue)}
                                    noOptionsText="Aucune catégorie ne correspond à votre recherche"
                                    getOptionLabel={(option: any) =>
                                        option?.category?.name && option?.name
                                            ? `${option.category.name} (${option.name})`
                                            : ''
                                    }
                                    // Comparaison basée sur le slug
                                    isOptionEqualToValue={(option, value) =>
                                        option.slug === value.slug
                                    }
                                    renderOption={(props, option) => (
                                        <AutocompleteOption {...props}>
                                            {option?.category?.name} ({option?.name})
                                        </AutocompleteOption>
                                    )}
                                />
                            </FormControl>
                        </Card>
                    </Grid>

                    {recommendedCategories.length > 0 && (
                        <Grid xs={12}>
                            <Card variant="soft" sx={{ mx: 5, p: 5, mt: 3 }}>
                                <Typography level="title-md" sx={{ mb: 2 }}>
                                    Recommandations
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: {
                                            xs: 'repeat(1, 1fr)',
                                            sm: 'repeat(2, 1fr)',
                                            md: 'repeat(3, 1fr)',
                                            lg: 'repeat(4, 1fr)',
                                            xl: 'repeat(4, 1fr)',
                                        },
                                        gap: '16px', // Espace entre les boutons
                                    }}
                                >
                                    {recommendedCategories.slice(0, 8).map((rec, idx) => {
                                        const subCatName = rec?.subCategory?.properties?.name;
                                        const catName = rec?.category?.properties?.name;
                                        const recSlug = rec?.subCategory?.properties?.slug;
                                        if (!subCatName || !catName || !recSlug) return null;
                                        return (
                                            <Button
                                                key={`${recSlug}-${idx}`}
                                                size="sm"
                                                onClick={() => handleAddRecommendedCategory(rec)}
                                                sx={{
                                                    width: '100%',
                                                    height: '80px',
                                                    padding: '16px',
                                                    boxSizing: 'border-box',
                                                }}
                                            >
                                                {catName} ({subCatName})
                                            </Button>
                                        );
                                    })}
                                </Box>
                            </Card>
                        </Grid>
                    )}




                </Grid>
            </Card>
        </>
    );
};

export default CategoriesStep;